import { getEnumeratedTitle } from '@src/components/Layout/utils/utils';
import { getDefaultPanelConfig } from '@src/providers/AppLayoutContextProvider/utils/getDefaultPanelConfig';
import { getLayoutGroupByPanelId } from '@src/providers/AppLayoutContextProvider/utils/getLayoutGroupByPanelId';
import type { PanelComponentSpec } from '@src/types/LayoutConfig';
import type { Security } from '@talos/kyoko';
import type { SerializedDockview } from 'dockview';
import { v1 as uuid } from 'uuid';
import type { MarketTabPanelType } from '../marketTabPanelTypes';

export type MarketPanelComponentSpec = PanelComponentSpec & { type: MarketTabPanelType };

interface AddMarketPanelParams {
  panelType: MarketTabPanelType;
  panelSpecs: readonly MarketPanelComponentSpec[];
  sourceDockViewLayout: SerializedDockview;
  targetPanelId: string;
  searchableSecurities: Security[];
}

export function getLayoutWithAddedMarketPanel({
  panelType,
  panelSpecs,
  sourceDockViewLayout,
  targetPanelId,
  searchableSecurities,
}: AddMarketPanelParams): { dockViewLayout: SerializedDockview; newPanelId: string } | undefined {
  const panelSpec = panelSpecs.find(spec => spec.type === panelType);
  if (panelSpec == null) {
    return undefined;
  }

  const dockViewLayout = structuredClone(sourceDockViewLayout);
  const targetGroup = getLayoutGroupByPanelId(dockViewLayout, targetPanelId);
  if (targetGroup == null) {
    return undefined;
  }

  const newPanelId = uuid();
  const { defaultParams, defaultTitle } = getDefaultPanelConfig(panelSpec.type, searchableSecurities);
  // Mobile routes render from serialized layout config, not a live Dockview group, so add directly here.
  dockViewLayout.panels[newPanelId] = {
    id: newPanelId,
    contentComponent: panelSpec.type,
    title: getEnumeratedTitle(defaultTitle ?? panelSpec.label, Object.values(dockViewLayout.panels)),
    params: {
      ...defaultParams,
      type: panelSpec.type,
    },
  };
  targetGroup.data.views.push(newPanelId);
  targetGroup.data.activeView = newPanelId;

  return { dockViewLayout, newPanelId };
}
